import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, AlertTriangle, Loader2, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API } from '../App';

const ConfirmSpot = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [status, setStatus] = useState('loading'); // loading, success, already_confirmed, error
  const [message, setMessage] = useState('');
  const [gameTitle, setGameTitle] = useState('');
  const [gameId, setGameId] = useState('');

  useEffect(() => {
    const confirmSpot = async () => {
      if (!token) {
        setStatus('error');
        setMessage('No confirmation token provided.');
        return;
      }

      try {
        const response = await fetch(`${API}/confirm/token?token=${encodeURIComponent(token)}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-OpenRuns-Client-Protocol': '2'
          }
        });

        const data = await response.json();

        if (response.ok) {
          if (data.status === 'already_confirmed') {
            setStatus('already_confirmed');
            setMessage(data.message || "You're all set! Your spot was already confirmed.");
          } else {
            setStatus('success');
            setMessage(data.message || 'Your spot is confirmed! See you at the game.');
          }
          setGameTitle(data.game_title || '');
          setGameId(data.game_id || '');
        } else {
          setStatus('error');
          setMessage(data.detail || data.message || 'Unable to confirm your spot.');
        }
      } catch (err) {
        console.error('Confirmation error:', err);
        setStatus('error');
        setMessage('Something went wrong. Please try again or contact support.');
      }
    };

    confirmSpot();
  }, [token]);

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <div className="text-center" data-testid="confirm-loading">
            <Loader2 className="w-16 h-16 mx-auto mb-6 text-orange-500 animate-spin" />
            <h1 className="text-2xl font-bold text-zinc-900 mb-2">Confirming your spot...</h1>
            <p className="text-zinc-600">Please wait a moment.</p>
          </div>
        );

      case 'success':
        return (
          <div className="text-center" data-testid="confirm-success">
            <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-zinc-900 mb-2">You're In!</h1>
            <p className="text-zinc-600 mb-6">{message}</p>
            {gameTitle && (
              <p className="text-lg font-semibold text-orange-600 mb-6">{gameTitle}</p>
            )}
            <Button
              onClick={() => navigate(gameId ? `/games/${gameId}` : '/dashboard')}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="view-game-btn"
            >
              View Game Details
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        );

      case 'already_confirmed':
        return (
          <div className="text-center" data-testid="confirm-already">
            <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-zinc-900 mb-2">Already Confirmed!</h1>
            <p className="text-zinc-600 mb-6">{message}</p>
            {gameTitle && (
              <p className="text-lg font-semibold text-orange-600 mb-6">{gameTitle}</p>
            )}
            <Button
              onClick={() => navigate(gameId ? `/games/${gameId}` : '/dashboard')}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="view-game-btn"
            >
              View Game Details
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        );

      case 'error':
        return (
          <div className="text-center" data-testid="confirm-error">
            <div className="w-20 h-20 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
              {message.includes('expired') ? (
                <AlertTriangle className="w-12 h-12 text-amber-600" />
              ) : (
                <XCircle className="w-12 h-12 text-red-600" />
              )}
            </div>
            <h1 className="text-2xl font-bold text-zinc-900 mb-2">
              {message.includes('expired') ? 'Link Expired' : 'Unable to Confirm'}
            </h1>
            <p className="text-zinc-600 mb-6">{message}</p>
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-orange-500 hover:bg-orange-600 w-full"
                data-testid="go-dashboard-btn"
              >
                Go to Dashboard
              </Button>
              <p className="text-sm text-zinc-500">
                If you need help, please contact us at{' '}
                <a href="mailto:support@openrunsusa.com" className="text-orange-600 hover:underline">
                  support@openrunsusa.com
                </a>
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-50 to-white flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-lg p-8 border border-zinc-100">
          {/* Logo */}
          <div className="text-center mb-8">
            <span className="text-2xl font-bold text-orange-500">Open Runs</span>
          </div>
          
          {renderContent()}
        </div>
        
        {/* Footer */}
        <p className="text-center text-xs text-zinc-400 mt-6">
          Open Runs USA - Find and organize basketball games
        </p>
      </div>
    </div>
  );
};

export default ConfirmSpot;
